import { useTranslation } from 'react-i18next';

import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import { valueFor } from '@/lib/forms/translatable';

import type { PageSectionRow } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §5.2 — "a live thumbnail of what that section
 * looks like so the editor knows what they are changing".
 *
 * A schematic rather than a render. The real section is a React component in
 * `apps/web` with the site's fonts, its container width and its data; importing
 * it here would mean the admin bundling the public app, and shrinking it to
 * thumbnail size would produce something the editor still could not read.
 *
 * What an editor actually needs from this is *which* section they are on and
 * where their text will land in it — a hero's second title line is gold and
 * enormous, a card grid's heading sits above four boxes — so the schematic
 * draws the layout in grey and the editor's own words on top of it. It updates
 * as they type, which is the "live" half.
 */
export function SectionThumbnail({ section }: { section: PageSectionRow }) {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();

  const heading = valueFor(section.heading, locale);
  const subheading = valueFor(section.subheading, locale);

  return (
    <figure className="bg-surface-subtle rounded-card border border-subtle p-3">
      <figcaption className="mb-2 text-caption text-muted">{t('pageSections.preview')}</figcaption>

      <div
        className="overflow-hidden rounded-lg bg-surface p-3"
        // Decorative: everything in it is either repeated from the form beside
        // it or a grey box.
        aria-hidden="true"
      >
        {section.section_key === 'hero' ? (
          <Hero section={section} heading={heading} />
        ) : (
          <Band section={section} heading={heading} subheading={subheading} />
        )}
      </div>
    </figure>
  );
}

function Hero({ section, heading }: { section: PageSectionRow; heading: string }) {
  const data = section.data;

  return (
    <div className="space-y-2 rounded-md bg-gradient-to-br from-navy-700 to-navy-500 p-4">
      <div className="h-1.5 w-16 rounded-full bg-white/40" />

      <p className="text-body-sm font-extrabold leading-tight text-white">
        {heading || readString(data, 'title_line_1')}
      </p>

      {/* §5.2 — "title line 2 (gold)". The one thing about the hero an editor
          cannot see from the form alone. */}
      <p className="text-body-sm font-extrabold leading-tight text-gold-400">
        {readString(data, 'title_line_2')}
      </p>

      <div className="flex gap-1.5 pt-1">
        {countOf(data, 'ctas', 2).map((index) => (
          <span
            key={index}
            className={
              index === 0
                ? 'h-4 w-14 rounded-full bg-white'
                : 'h-4 w-14 rounded-full border border-white/60'
            }
          />
        ))}
      </div>

      <div className="flex gap-3 pt-2">
        {countOf(data, 'stats', 3).map((index) => (
          <span key={index} className="space-y-1">
            <span className="block h-2 w-8 rounded bg-gold-400" />
            <span className="block h-1.5 w-10 rounded bg-white/40" />
          </span>
        ))}
      </div>
    </div>
  );
}

function Band({
  section,
  heading,
  subheading,
}: {
  section: PageSectionRow;
  heading: string;
  subheading: string;
}) {
  const items = itemCount(section);

  return (
    <div className="space-y-2">
      <p className="text-body-sm font-extrabold leading-tight text-primary">{heading || '—'}</p>

      {subheading ? <p className="line-clamp-2 text-caption text-muted">{subheading}</p> : null}

      {section.section_key === 'internationalisation' ? (
        <>
          <div className="flex gap-1.5 pt-1">
            {countOf(section.data, 'tabs', 3).map((index) => (
              <span
                key={index}
                className={
                  index === 0
                    ? 'h-4 w-12 rounded-full bg-navy-600'
                    : 'bg-surface-subtle h-4 w-12 rounded-full'
                }
              />
            ))}
          </div>

          <div className="bg-surface-subtle h-10 rounded" />
        </>
      ) : (
        <div className="grid grid-cols-4 gap-1.5 pt-1">
          {Array.from({ length: Math.min(items, 8) }, (_, index) => (
            <span key={index} className="bg-surface-subtle h-8 rounded" />
          ))}
        </div>
      )}
    </div>
  );
}

/** How many cards this section will draw, as far as its payload says. */
function itemCount(section: PageSectionRow): number {
  const limit = section.data.limit;

  return typeof limit === 'number' && limit > 0 ? limit : 4;
}

/** `[0, 1, …]` for however many of `key` there are, or `fallback` of them. */
function countOf(data: Record<string, unknown>, key: string, fallback: number): number[] {
  const value = data[key];
  const length = Array.isArray(value) && value.length > 0 ? value.length : fallback;

  return Array.from({ length: Math.min(length, fallback) }, (_, index) => index);
}

function readString(data: Record<string, unknown>, key: string): string {
  const value = data[key];

  if (typeof value === 'string') {
    return value;
  }

  // Translatable leaves are `{ka, en}`; the thumbnail is happy with either.
  if (typeof value === 'object' && value !== null) {
    const first = Object.values(value).find((entry) => typeof entry === 'string' && entry !== '');

    return typeof first === 'string' ? first : '';
  }

  return '';
}
